/**
 * The numbers behind the "Reach by address field" chart.
 *
 * The section asks three different questions of the same rows — how engaged was each
 * field, how much of the send was it, and what exactly happened to it — and each has its
 * own denominator. Keeping that arithmetic here, out of the component, is what makes it
 * testable: a wrong denominator does not look wrong on a dashboard, it just quietly
 * reports a confident false percentage.
 */

/**
 * One colour per address field — the ring, the composition bar, the table row and the
 * chip beside a recipient's name all read from here, so a field looks the same wherever
 * it appears.
 *
 * These are the three series colours the design system already carries (chartTheme's
 * teal / bronze / blueTeal), chosen there for separation under colour-vision deficiency.
 * Reusing them rather than inventing three keeps that property and keeps the section
 * looking like the rest of the app.
 */
export const CHANNEL_COLOR = { TO: '#0A9188', CC: '#B0872F', BCC: '#3E7C8C' };

/** Plain-language field names. */
export const CHANNEL_NAME = { TO: 'To', CC: 'CC', BCC: 'BCC' };

/** A percentage to one decimal; zero rather than NaN when there is nothing to divide by. */
const rate = (n, of) => (of > 0 ? Math.round((n / of) * 1000) / 10 : 0);

/** Every address the mail reached, across all the fields it used. */
export function totalReach(channels) {
  return (channels || []).reduce((n, c) => n + (Number(c?.total) || 0), 0);
}

/**
 * The channels as the chart wants them: send order, a colour each, the open rate the API
 * already computed (so the ring never disagrees with the numbers printed elsewhere), and
 * the field's share of the whole send for the composition bar.
 */
export function channelSeries(channels) {
  const reach = totalReach(channels);
  return (channels || []).map((c) => ({
    kind: c.kind,
    name: CHANNEL_NAME[c.kind] || c.kind,
    color: CHANNEL_COLOR[c.kind] || CHANNEL_COLOR.TO,
    total: Number(c.total) || 0,
    sent: Number(c.sent) || 0,
    delivered: Number(c.delivered) || 0,
    opened: Number(c.opened) || 0,
    clicked: Number(c.clicked) || 0,
    bounced: Number(c.bounced) || 0,
    failed: Number(c.failed) || 0,
    rate: Number(c.open_rate) || 0,
    share: rate(Number(c.total) || 0, reach),
  }));
}

/**
 * The campaign's open rate across every field — opens over deliveries, pooled.
 *
 * Deliberately not the mean of the per-field rates: a single opened address in the To line
 * scores 100% there, and averaging lets that one address weigh as much as a BCC list of
 * five hundred.
 */
export function overallOpenRate(channels) {
  const rows = channels || [];
  const opened = rows.reduce((n, c) => n + (Number(c?.opened) || 0), 0);
  const delivered = rows.reduce((n, c) => n + (Number(c?.delivered) || 0), 0);
  return rate(opened, delivered);
}
